/* The one place the cockpit talks to the network.
 *
 * Everything above this file — the transport, the mappers, the screens — deals
 * in domain types and never sees a `Response`, a header or a status code. That
 * boundary keeps screens independent of protocol details and ensures every
 * caller sees the same normalized `ApiError`.
 *
 * Four things live here and nowhere else:
 *
 *   - the base URL, read once from `VITE_API_BASE_URL`;
 *   - the correlation id, minted per request and echoed back by the service;
 *   - the ETag, captured on read so a later write can send `If-Match`;
 *   - the TMF `Error` body, turned into a typed exception.
 */

/** The TMF630 error body the service returns for every 4xx and 5xx.
 *
 * Declared here rather than imported from the generated types: this is the one
 * shape the client must be able to parse *before* it can trust that it is
 * talking to our service at all. A proxy returning HTML for a 502 has to fail
 * the same way as a real error, and that means the parser cannot depend on the
 * body matching a generated schema.
 */
interface TmfErrorBody {
  readonly code?: string;
  readonly reason?: string;
  readonly message?: string;
  readonly status?: string;
  readonly referenceError?: string;
  readonly '@details'?: Readonly<Record<string, unknown>>;
}

/**
 * A failed request, carrying enough to say what happened and what to do.
 *
 * `RouteError` in the app shell renders `message`, so it has to be a sentence a
 * person can act on rather than "Request failed". `code` and `details` are for
 * the handful of places that branch on a specific failure — a 409 on a case
 * write is the concurrency handshake, not a generic error.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly reason: string;
  readonly correlationId: string | null;
  readonly details: Readonly<Record<string, unknown>>;

  constructor(init: {
    status: number;
    code: string;
    reason: string;
    message: string;
    correlationId: string | null;
    details?: Readonly<Record<string, unknown>>;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.reason = init.reason;
    this.correlationId = init.correlationId;
    this.details = init.details ?? {};
  }

  /** True for the stale-write case, which callers retry rather than report. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** True when the write was refused for want of an `If-Match`. A bug in the
      caller rather than a state the user can resolve, so it is worth telling
      them apart. */
  get isPreconditionRequired(): boolean {
    return this.status === 428;
  }
}

/** Where the API is. Empty means "same origin", which is what the Vite dev
    proxy provides — see `vite.config.ts`. A trailing slash is trimmed so that
    joining a path that starts with `/` cannot produce a double slash, which
    some proxies treat as a different route. */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** The service's own URL for `path`.
 *
 * For the two places that *navigate* to the service rather than fetch from it —
 * `/auth/login` and `/auth/logout` are top-level redirects into Entra ID, not
 * requests whose response the cockpit reads. They still have to be addressed
 * the same way every other call is, or a deployment that moves the service
 * would move its data and leave its sign-in behind. */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/** Correlation ids are echoed in the service's logs and in every error body, so
    a support conversation can start from a single string. `randomUUID` is
    available in every browser the app targets; the fallback exists for test
    environments that stub `crypto` incompletely. */
function correlationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** What a read returns: the body, and the version to write back with.
 *
 * The ETag is carried beside the body rather than folded into it because it
 * belongs to the HTTP exchange, not to the resource — a projected body under
 * `?fields=` is still addressed by the same version. */
export interface Fetched<T> {
  readonly data: T;
  readonly etag: string | null;
  /** `X-Total-Count`, when the service sent one. The list endpoints always do;
      it is null for single reads. */
  readonly total: number | null;
}

export interface RequestOptions {
  /** Query parameters. Undefined and null values are dropped rather than sent
      as the strings "undefined" and "null". */
  readonly query?: Readonly<Record<string, string | number | boolean | undefined | null>>;
  /** JSON request body, for POST and PATCH. */
  readonly body?: unknown;
  /** Multipart request body. The browser owns its boundary and Content-Type. */
  readonly multipart?: FormData;
  /** The version this write expects to be amending. Sent as `If-Match`; the
      service answers 428 without it and 409 when it is stale. */
  readonly ifMatch?: string | null;
  /** `Content-Type` override. The merge-PATCH endpoints need
      `application/merge-patch+json`, which is the whole reason this exists. */
  readonly contentType?: string;
  readonly signal?: AbortSignal;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return `${BASE_URL}${path}${qs ? `?${qs}` : ''}`;
}

/** Turn a failed response into an `ApiError`, whatever it actually contains.
 *
 * The happy path is a TMF `Error` body. The unhappy paths — a proxy's HTML 502,
 * an empty 504, a CORS-mangled response — have to produce the same class, or
 * every caller needs a second branch for "the error was not an error". */
async function toApiError(response: Response, correlation: string | null): Promise<ApiError> {
  let body: TmfErrorBody = {};
  try {
    const parsed: unknown = await response.json();
    if (parsed !== null && typeof parsed === 'object') {
      body = parsed as TmfErrorBody;
    }
  } catch {
    // Not JSON. The status line is all there is, and it is enough.
  }

  const reason = body.reason ?? response.statusText ?? 'Request failed';
  return new ApiError({
    status: response.status,
    code: body.code ?? `http${response.status}`,
    reason,
    // `message` carries the detail when the service sent one, and falls back to
    // `reason` so the error boundary never renders an empty string.
    message: body.message ?? reason,
    correlationId: correlation,
    details: body['@details'] ?? {},
  });
}

/**
 * One request, one place.
 *
 * Every method on the transport goes through here, so anything true of all
 * requests — the correlation header, the JSON accept, the error translation —
 * is written once and cannot be forgotten on the twentieth endpoint.
 */
export async function request<T>(
  /* `DELETE` is here for exactly one route — withdrawing a tenant grant — and
     that route answers with the withdrawn grant rather than a 204. Nothing in
     this API deletes a row; the verb is the one HTTP has for "withdraw this",
     and the body coming back is what lets the screen show when it happened and
     who did it without a second read. `PUT` is here for the one config-admin
     route that replaces an MRI field set under `If-Match` — a whole-set
     overwrite, not a patch. */
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<Fetched<T>> {
  const correlation = correlationId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlation,
  };
  if (options.body !== undefined && options.multipart !== undefined) {
    throw new TypeError('A request cannot have both a JSON body and a multipart body.');
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = options.contentType ?? 'application/json';
  }
  if (options.ifMatch) {
    headers['If-Match'] = options.ifMatch;
  }

  // Built key by key rather than as one literal: under
  // `exactOptionalPropertyTypes` a `body: undefined` is a *present* key holding
  // undefined, which `RequestInit` does not accept. Omitting the key is what
  // "this request has no body" actually means.
  //
  // `credentials: 'include'` is what carries the session. The cookie is
  // HttpOnly, so nothing here can read it or attach it by hand — the only lever
  // the cockpit has is telling `fetch` to send it. Same-origin behind the Vite
  // proxy this changes nothing; cross-origin it is the difference between an
  // authenticated request and a 401, and it pairs with the service's
  // `allow_credentials=True`.
  const init: RequestInit = { method, headers, credentials: 'include' };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.multipart !== undefined) init.body = options.multipart;
  if (options.signal !== undefined) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    // The service is unreachable, not merely unhappy. Status 0 says so without
    // pretending to be an HTTP code the service never sent.
    throw new ApiError({
      status: 0,
      code: 'networkUnavailable',
      reason: 'The service could not be reached',
      message:
        'Could not reach the CloudOrder service. It may be stopped, or the ' +
        'network may be down.',
      correlationId: correlation,
      details: { cause: String(cause) },
    });
  }

  if (!response.ok) {
    throw await toApiError(response, response.headers.get('X-Correlation-Id') ?? correlation);
  }

  const total = response.headers.get('X-Total-Count');
  return {
    // 204 has no body; nothing in this app returns one today, but a `json()` on
    // an empty response throws, and that would be a confusing way to find out.
    data: (response.status === 204 ? undefined : await response.json()) as T,
    etag: response.headers.get('ETag'),
    total: total === null ? null : Number(total),
  };
}

/** The common case: a read whose ETag and count nobody needs. */
export async function get<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await request<T>('GET', path, options)).data;
}

/** Read every page of a TMF collection using its count headers.
 *
 * The service clamps an oversized limit to its configured maximum. Advancing by
 * the number actually returned therefore works without duplicating that server
 * setting in the app, while `X-Total-Count` tells us when the collection is
 * complete.
 */
export async function getCollection<T>(
  path: string,
  options: RequestOptions = {},
): Promise<readonly T[]> {
  const data: T[] = [];
  let offset = 0;
  let total: number | null = null;

  do {
    const page = await request<T[]>('GET', path, {
      ...options,
      query: { ...options.query, offset },
    });

    if (page.total === null || !Number.isSafeInteger(page.total) || page.total < 0) {
      throw new ApiError({
        status: 502,
        code: 'invalidPaginationMetadata',
        reason: 'The collection response has invalid pagination metadata',
        message: `The service did not return a valid X-Total-Count for ${path}.`,
        correlationId: null,
      });
    }
    if (total === null) total = page.total;

    data.push(...page.data);
    offset += page.data.length;

    if (page.data.length === 0 && offset < total) {
      throw new ApiError({
        status: 502,
        code: 'incompletePaginatedResponse',
        reason: 'The collection ended before its reported total',
        message: `The service returned ${offset} of ${total} rows for ${path}.`,
        correlationId: null,
      });
    }
  } while (offset < total);

  return data;
}
